import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import i18n from '../../../i18n';
import { scenes } from '@/config/siteImages';

export default function HeroSection() {
  const { t } = useTranslation();
  const isEs = i18n.language === 'es';

  const stats = [
    { icon: 'ri-award-line', label: t('sllc_hero_stat_1') },
    { icon: 'ri-line-chart-line', label: t('sllc_hero_stat_2') },
    { icon: 'ri-heart-line', label: t('sllc_hero_stat_3') },
  ];

  return (
    <section className="bg-[#F8F3FB] pt-20 sm:pt-24 lg:pt-32 pb-14 lg:pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative background shapes */}
      <div className="absolute top-0 right-0 w-[400px] lg:w-[520px] h-[400px] lg:h-[520px] rounded-full bg-[#E9D9F2]/40 translate-x-40 lg:translate-x-48 -translate-y-40 lg:-translate-y-48 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-56 lg:w-72 h-56 lg:h-72 rounded-full bg-[#E9D9F2]/30 -translate-x-28 lg:-translate-x-36 translate-y-28 lg:translate-y-36 pointer-events-none" />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">

          {/* Left: Text */}
          <div>
            {/* Eyebrow */}
            <div className="flex items-center gap-2 mb-4 sm:mb-5">
              <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full bg-[#4F2D63]/10 border border-[#4F2D63]/20 text-[#4F2D63] text-xs font-semibold uppercase tracking-widest">
                <i className="ri-building-4-line text-xs" />
                {t('sllc_eyebrow')}
              </span>
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0F766E]/10 text-[#0F766E] text-xs font-semibold mb-4 sm:mb-5">
              <i className="ri-shield-check-line text-xs" />
              {t('sllc_hero_badge')}
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight mb-4 sm:mb-5">
              {t('sllc_hero_title_1')}<br />
              <span className="text-[#4F2D63]">{t('sllc_hero_title_2')}</span>
            </h1>

            {/* Subtitle */}
            <p className="text-sm sm:text-base lg:text-lg text-gray-500 leading-relaxed mb-6 sm:mb-8">
              {t('sllc_hero_subtitle')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <Link
                to={isEs ? '/es/contacto' : '/contact'}
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('sllc_hero_cta_primary')} <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to={isEs ? '/es/como-funciona' : '/how-it-works'}
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 rounded-xl border border-[#E9D9F2] text-gray-700 hover:bg-white hover:border-[#4F2D63]/30 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('sllc_hero_cta_secondary')}
              </Link>
            </div>

            {/* Compliance */}
            <p className="text-xs text-gray-400 italic mb-6 sm:mb-8">
              {t('sllc_hero_compliance')}
            </p>

            {/* Mobile-only visual cards */}
            <div className="lg:hidden grid grid-cols-2 gap-3 mb-6">
              <div className="bg-white rounded-xl p-4 border border-[#E9D9F2]">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F8F3FB] mb-2.5">
                  <i className="ri-building-4-line text-[#4F2D63] text-sm" />
                </div>
                <p className="text-base font-bold text-gray-900 leading-none mb-0.5">
                  {isEs ? 'Semana 1–2' : 'Week 1–2'}
                </p>
                <p className="text-xs text-gray-500">
                  {isEs ? 'Forma tu LLC' : 'Form your LLC'}
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E9D9F2]">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F0FDF4] mb-2.5">
                  <i className="ri-line-chart-line text-[#0F766E] text-sm" />
                </div>
                <p className="text-base font-bold text-gray-900 leading-none mb-0.5">
                  {isEs ? 'Mes 3–9+' : 'Month 3–9+'}
                </p>
                <p className="text-xs text-gray-500">
                  {isEs ? 'Accede al capital' : 'Access funding'}
                </p>
              </div>
              <div className="col-span-2 bg-white rounded-xl px-4 py-3 border border-[#E9D9F2] flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 flex-shrink-0">
                  <i className="ri-star-fill text-amber-500 text-sm" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 leading-snug">
                    {isEs ? 'Sin LLC requerida para empezar' : 'No LLC required to begin'}
                  </p>
                  <p className="text-xs text-gray-400">
                    {isEs ? 'Te guiamos desde el primer paso' : 'We guide you from step one'}
                  </p>
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-3 sm:gap-5 pt-5 sm:pt-6 border-t border-[#E9D9F2]">
              {stats.map((stat) => (
                <div key={stat.label} className="flex items-center gap-2">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg bg-white border border-[#E9D9F2]">
                    <i className={`${stat.icon} text-[#4F2D63] text-xs sm:text-sm`} />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-600">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Visual card stack — desktop only */}
          <div className="hidden lg:flex flex-col gap-4">
            <div className="rounded-2xl overflow-hidden border border-[#E9D9F2] w-full h-64">
              <img
                src={scenes.sllcHero}
                alt="Entrepreneur starting a business"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-5 border border-[#E9D9F2]">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#F8F3FB] mb-3">
                  <i className="ri-building-4-line text-[#4F2D63] text-base" />
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-0.5">
                  {isEs ? 'Semana 1–2' : 'Week 1–2'}
                </p>
                <p className="text-xs text-gray-500">
                  {isEs ? 'Forma tu LLC' : 'Form your LLC'}
                </p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-[#E9D9F2]">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#F0FDF4] mb-3">
                  <i className="ri-line-chart-line text-[#0F766E] text-base" />
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-0.5">
                  {isEs ? 'Mes 3–9+' : 'Month 3–9+'}
                </p>
                <p className="text-xs text-gray-500">
                  {isEs ? 'Accede al capital' : 'Access funding'}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl px-5 py-4 border border-[#E9D9F2] flex items-center gap-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-50 flex-shrink-0">
                <i className="ri-star-fill text-amber-500 text-base" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">
                  {isEs ? 'Sin LLC requerida para empezar' : 'No LLC required to begin'}
                </p>
                <p className="text-xs text-gray-400">
                  {isEs ? 'Te guiamos desde el primer paso' : 'We guide you from step one'}
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
